import { useMemo, useState, type CSSProperties, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import type { BomCategory } from "../../api/structure";
import {
  hasExcludedAncestor, setExpectedType, setIncluded, setKind, setName, visibleIds, type ImportItem, type ImportState,
} from "../../lib/structureImport";
import { Select, type SelectOption } from "../../components/Select";
import Ic from "../../components/icons.jsx";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

/** Short label of why a line starts unticked. */
export function categoryLabel(t: Translate, category: BomCategory): string {
  switch (category) {
    case "OTHER_ID": return t("structureImport.category.otherId", "ID nije 6 cifara");
    case "NO_ID": return t("structureImport.category.noId", "bez ID broja");
  }
}

/**
 * The previewed bill of materials (sprint 5j): one row per line, indented by
 * level. A tick decides whether the line is imported — unticking a line takes
 * everything below it along — the name is changed by clicking it, the kind in
 * the list on the right, and next to it the part type the position will expect
 * (2026-10-20). `readOnly` (an imported draft, 2026-10-20) only shows it.
 */
export function ImportTree({
  state, collapsed, onToggleCollapsed, onChange, kindOptions, typeOptions, holdsAsset, readOnly = false,
}: {
  state: ImportState;
  collapsed: ReadonlySet<string>;
  onToggleCollapsed: (id: string) => void;
  onChange: (next: ImportState) => void;
  kindOptions: SelectOption<string>[];
  /** The part types to pick from. */
  typeOptions: SelectOption<string>[];
  /** Whether a position kind holds an asset — only those expect a part type. */
  holdsAsset: (kind: string) => boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const ids = useMemo(() => visibleIds(state, collapsed), [state, collapsed]);
  return (
    <div className="import-tree" role="list" aria-label={t("structureImport.treeAria", "Struktura iz fajla")}>
      {ids.map((id) => (
        <ImportRow
          key={id}
          item={state.items[id]}
          blocked={hasExcludedAncestor(state, id)}
          open={!collapsed.has(id)}
          kindOptions={kindOptions}
          typeOptions={typeOptions}
          holdsAsset={holdsAsset}
          readOnly={readOnly}
          onToggleOpen={onToggleCollapsed}
          onInclude={(itemId, included) => onChange(setIncluded(state, itemId, included))}
          onName={(itemId, name) => onChange(setName(state, itemId, name))}
          // A kind that holds no asset expects no part type: the type goes with the change.
          onKind={(itemId, kind) => onChange(holdsAsset(kind) ? setKind(state, itemId, kind) : setExpectedType(setKind(state, itemId, kind), itemId, null))}
          onType={(itemId, typeId) => onChange(setExpectedType(state, itemId, typeId || null))}
        />
      ))}
    </div>
  );
}

const fold = (s: string) => s.toUpperCase().replace(/[\s_]+/g, " ").trim();

function ImportRow({
  item, blocked, open, kindOptions, typeOptions, holdsAsset, readOnly, onToggleOpen, onInclude, onName, onKind, onType,
}: {
  item: ImportItem;
  /** Something above is unticked: this line is out whatever its own tick says. */
  blocked: boolean;
  open: boolean;
  kindOptions: SelectOption<string>[];
  typeOptions: SelectOption<string>[];
  holdsAsset: (kind: string) => boolean;
  readOnly: boolean;
  onToggleOpen: (id: string) => void;
  onInclude: (id: string, included: boolean) => void;
  onName: (id: string, name: string) => void;
  onKind: (id: string, kind: string) => void;
  onType: (id: string, typeId: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);
  const out = blocked || !item.included;
  const lockedReason = readOnly ? t("structureImport.lockedImported", "Nacrt je uvezen — više se ne menja.") : undefined;
  const takesType = item.kind !== "" && holdsAsset(item.kind);
  // Empty shows the hint; once a type is picked the list offers taking it back.
  const typeChoices = useMemo(
    () => (item.expectedTypeId ? [{ value: "", label: t("structureImport.noType", "— bez tipa —") }, ...typeOptions] : typeOptions),
    [item.expectedTypeId, typeOptions, t],
  );
  const hasChildren = item.childIds.length > 0;
  // The name starts from the drawing (columns A/B); the other source stays one click away while editing.
  const suggestions = [
    item.title && fold(item.title) !== fold(item.name)
      ? { name: item.title, label: t("structureImport.useTitle", "Naziv iz crteža: {{title}}", { title: item.title }) }
      : null,
    fold(item.model) !== fold(item.name) && (!item.title || fold(item.model) !== fold(item.title))
      ? { name: item.model, label: t("structureImport.useModel", "Ime modela: {{model}}", { model: item.model }) }
      : null,
  ].filter((s): s is { name: string; label: string } => s !== null);
  const meta = [
    item.drawingCode && t("structureImport.drawing", "crtež {{code}}", { code: item.drawingCode }),
    item.title && fold(item.title) !== fold(item.name) ? item.title : null,
    fold(item.model) !== fold(item.name) ? item.model : null,
    item.material,
  ].filter(Boolean).join(" · ");

  function commit() {
    const next = draft.trim();
    if (next && next !== item.name) onName(item.id, next);
    setEditing(false);
  }

  function onBlur(e: FocusEvent<HTMLInputElement>) {
    // Clicking the suggestion moves the focus to it; that click decides the name.
    if ((e.relatedTarget as HTMLElement | null)?.dataset.importSuggestion) return;
    commit();
  }

  return (
    <div className={`import-row${out ? " is-out" : ""}`} role="listitem" style={{ "--depth": item.depth } as CSSProperties}>
      {hasChildren ? (
        <button
          type="button"
          className="tree-toggle"
          data-open={open}
          aria-expanded={open}
          aria-label={open
            ? t("structureImport.collapse", "Skupi {{name}}", { name: item.name })
            : t("structureImport.expand", "Raširi {{name}}", { name: item.name })}
          onClick={() => onToggleOpen(item.id)}
        >
          <Ic.chev />
        </button>
      ) : <span aria-hidden="true" />}
      <label className="import-check" title={lockedReason}>
        <input
          type="checkbox"
          checked={item.included}
          disabled={blocked || readOnly}
          onChange={(e) => onInclude(item.id, e.target.checked)}
          aria-label={t("structureImport.include", "Uvezi {{name}}", { name: item.name })}
        />
      </label>
      <div className="import-row-main">
        {editing ? (
          <span className="import-name-edit">
            <input
              className="input"
              value={draft}
              maxLength={200}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the field replaces the name the user just clicked
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onBlur={onBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commit(); }
                if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setEditing(false); }
              }}
              aria-label={t("structureImport.nameOf", "Naziv pozicije za {{model}}", { model: item.model })}
            />
            {suggestions.map((s) => (
              <button
                key={s.name}
                type="button"
                className="chip"
                data-import-suggestion="true"
                onClick={() => { onName(item.id, s.name); setEditing(false); }}
              >
                {s.label}
              </button>
            ))}
          </span>
        ) : readOnly ? (
          <span className="import-name is-static"><span>{item.name}</span></span>
        ) : (
          <button
            type="button"
            className="import-name"
            onClick={() => { setDraft(item.name); setEditing(true); }}
            title={t("structureImport.rename", "Promeni naziv")}
          >
            <span>{item.name}</span>
            <Ic.pencil aria-hidden="true" />
          </button>
        )}
        {item.quantity > 1 && <span className="tag neutral">× {item.quantity}</span>}
        {item.category && <span className="tag warn">{categoryLabel(t, item.category)}</span>}
        {hasChildren && !open && (
          <span className="muted import-row-meta">{t("structureImport.hiddenBelow", "+{{count}} ispod", { count: item.childIds.length })}</span>
        )}
        {meta && <span className="import-row-meta">{meta}</span>}
      </div>
      <div className="import-row-kind" title={lockedReason}>
        <Select<string>
          value={item.kind}
          options={kindOptions}
          onChange={(kind) => onKind(item.id, kind)}
          disabled={out || readOnly}
          placeholder={t("structureImport.pickKind", "Vrsta…")}
          ariaLabel={t("structureImport.kindOf", "Vrsta pozicije za {{name}}", { name: item.name })}
        />
      </div>
      {/* A kind that holds no asset has no part type: no list at all, the cell stays to keep the columns. */}
      <div className="import-row-type" title={lockedReason}>
        {takesType && (
          <Select<string>
            value={item.expectedTypeId ?? ""}
            options={typeChoices}
            onChange={(typeId) => onType(item.id, typeId)}
            disabled={out || readOnly}
            placeholder={t("structureImport.pickType", "Tip dela…")}
            ariaLabel={t("structureImport.typeOf", "Tip dela za {{name}}", { name: item.name })}
          />
        )}
      </div>
    </div>
  );
}
